import { useEditorStore, type ClickEffectType, type CursorStyle } from '@/store/editor-store'
import { useRecordingStore } from '@/store/recording-store'
import { Cursor01Icon as MousePointer, Cursor02Icon as MousePointerClick, ViewIcon as Eye, ViewOffIcon as EyeOff, ComputerIcon as Monitor, RefreshIcon as RefreshCw, Clock01Icon as Timer } from 'hugeicons-react'
import { useState, useEffect } from 'react'

const CLICK_EFFECTS: { value: ClickEffectType; label: string }[] = [
    { value: 'none', label: 'None' },
    { value: 'ripple', label: 'Ripple' },
    { value: 'pulse', label: 'Pulse' },
    { value: 'ring', label: 'Ring' },
]

const CURSOR_SHAPES: { value: CursorStyle; label: string; preview: string }[] = [
    { value: 'circle', label: 'Circle', preview: '⬤' },
    { value: 'ring', label: 'Ring', preview: '◯' },
    { value: 'default', label: 'Arrow', preview: '↖' },
    { value: 'pointer', label: 'Hand', preview: '👆' },
    { value: 'dot', label: 'Dot', preview: '•' },
    { value: 'crosshair', label: 'Cross', preview: '+' },
]

export default function CursorControls() {
    const { cursorSettings, updateCursorSettings } = useEditorStore()
    const mouseData = useRecordingStore((s) => s.mouseData)
    const isExtension = useRecordingStore((s) => s.isExtension)
    const isDesktop = useRecordingStore((s) => s.isDesktop)

    const hasOverlay = true // Allow cursor overlay & customization on Web App, Extension, and Desktop
    const clickCount = mouseData?.filter((e) => e.type === 'click').length || 0
    const [recalibrating, setRecalibrating] = useState(false)
    const [predictMs, setPredictMs] = useState(25)

    useEffect(() => {
        if (!isDesktop) return
        ;(window as any).electron?.getSetting?.('cursorPredictMs').then((v: number | null) => {
            if (v != null) setPredictMs(v)
        })
    }, [isDesktop])

    async function handleRecalibrate() {
        setRecalibrating(true)
        await (window as any).electron?.recalibrateCursor?.()
        setTimeout(() => setRecalibrating(false), 800)
    }

    function adjustPredictMs(delta: number) {
        const next = Math.max(-50, Math.min(100, predictMs + delta))
        setPredictMs(next)
        ;(window as any).electron?.setCursorPredictMs?.(next)
        ;(window as any).electron?.setSetting?.('cursorPredictMs', next)
    }

    // ── Early Return for Web ────────────────────────────────────────────────
    // Web browsers sandbox tabs, making cross-tab mouse tracking impossible.
    // Instead of showing broken controls, we use this tab to upsell the extension/desktop app.
    if (!isExtension && !isDesktop) {
        return (
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <MousePointer size={16} className="text-brand-400" />
                    <span className="text-sm font-semibold text-white">Cursor Controls</span>
                </div>
                
                <div className="bg-surface-800/60 border border-white/5 rounded-xl p-4 flex flex-col gap-3 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 text-brand-400 pointer-events-none transition-transform group-hover:scale-110">
                        <MousePointer size={64} />
                    </div>
                    
                    <h3 className="text-sm font-semibold text-white">Unlock Custom Cursors ✨</h3>
                    <p className="text-[11px] text-txt-secondary leading-relaxed">
                        Standard web browsers prevent screen recorders from tracking your mouse on other tabs for security reasons. 
                    </p>
                    <p className="text-[11px] text-txt-secondary leading-relaxed">
                        To add gorgeous custom cursors, scaling, and click effects to your videos, download our free app or extension.
                    </p>
                    
                    <div className="flex flex-col gap-2 mt-2">
                        <button className="flex items-center justify-center gap-2 w-full bg-brand-500 hover:bg-brand-600 text-white py-2 px-3 rounded-lg text-xs font-medium transition-colors shadow-lg shadow-brand-500/20">
                            Download Desktop App
                        </button>
                        <button className="flex items-center justify-center gap-2 w-full bg-white/5 hover:bg-white/10 text-white py-2 px-3 rounded-lg text-xs font-medium transition-colors border border-white/10">
                            Install Chrome Extension
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <MousePointer size={16} className="text-brand-400" />
                    <span className="text-sm font-semibold text-white">Cursor Settings</span>
                </div>
                {hasOverlay && (
                    <button
                        onClick={() => updateCursorSettings({ visible: !cursorSettings.visible })}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${cursorSettings.visible
                            ? 'bg-brand-500/20 text-brand-400 border border-brand-500/30'
                            : 'bg-white/5 text-txt-dim border border-white/10 hover:bg-white/10'
                            }`}
                    >
                        {cursorSettings.visible ? <Eye size={12} /> : <EyeOff size={12} />}
                        {cursorSettings.visible ? 'Effects On' : 'Effects Off'}
                    </button>
                )}
            </div>

            {/* Extension note */}
            {isExtension && (
                <div className="text-[11px] text-txt-dim bg-surface-800/40 border border-white/5 rounded-lg px-3 py-2.5 leading-relaxed">
                    <>Cursor is baked into the recording. <strong className="text-brand-300 font-medium">Click effects & spotlight</strong> are added as an overlay.</>
                </div>
            )}

            {/* Desktop note */}
            {isDesktop && (
                <div className="space-y-2">
                    <div className="flex items-start gap-2 text-[10px] text-brand-400/70 bg-brand-500/5 border border-brand-500/10 rounded-lg p-2.5">
                        <Monitor size={12} className="mt-0.5 shrink-0 text-brand-400" />
                        <span>System cursor tracking active. Full cursor customization available.</span>
                    </div>
                    <button
                        onClick={handleRecalibrate}
                        disabled={recalibrating}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs text-txt-secondary hover:text-white bg-white/5 hover:bg-white/10 border border-white/5 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={12} className={recalibrating ? 'animate-spin' : ''} />
                        {recalibrating ? 'Recalibrating…' : 'Recalibrate Cursor Position'}
                    </button>

                    {/* Cursor timing offset — like subtitle time adjuster */}
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/5">
                        <Timer size={12} className="text-txt-dim shrink-0" />
                        <span className="text-xs text-txt-secondary flex-1">Cursor Timing</span>
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => adjustPredictMs(-5)}
                                className="w-6 h-6 flex items-center justify-center rounded-md text-txt-secondary hover:text-white hover:bg-white/10 transition-colors text-sm font-medium"
                            >−</button>
                            <span className="text-xs tabular-nums text-white w-12 text-center">
                                {predictMs > 0 ? `+${predictMs}` : predictMs}ms
                            </span>
                            <button
                                onClick={() => adjustPredictMs(5)}
                                className="w-6 h-6 flex items-center justify-center rounded-md text-txt-secondary hover:text-white hover:bg-white/10 transition-colors text-sm font-medium"
                            >+</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay controls — shared between web, extension & desktop */}
            {hasOverlay && (
                <>
                    {/* Mouse data stats */}
                    {mouseData.length > 0 && (
                        <div className="flex items-center gap-3 text-[11px] text-txt-dim">
                            <span>{mouseData.length.toLocaleString()} events recorded</span>
                            <span>•</span>
                            <span>{clickCount} clicks</span>
                        </div>
                    )}

                    {cursorSettings.visible && (
                        <>
                            {/* Shape, Size, Color (Hidden for Extension since cursor is baked in) */}
                            {!isExtension && (
                                <>
                                    {/* Cursor Shape */}
                                    <div className="space-y-2">
                                        <label className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Cursor Shape</label>
                                <div className="grid grid-cols-3 gap-1.5">
                                    {CURSOR_SHAPES.map((shape) => (
                                        <button
                                            key={shape.value}
                                            onClick={() => updateCursorSettings({ style: shape.value })}
                                            className={`py-2 px-1 rounded-xl text-center transition-all ${cursorSettings.style === shape.value
                                                ? 'bg-brand-500/15 border border-brand-500/40 text-white'
                                                : 'bg-white/5 border border-transparent text-txt-dim hover:bg-white/10'
                                                }`}
                                        >
                                            <div className="text-base leading-none mb-1">{shape.preview}</div>
                                            <div className="text-[9px]">{shape.label}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Size */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Size</label>
                                    <span className="text-[11px] text-txt-dim tabular-nums">{cursorSettings.size.toFixed(1)}x</span>
                                </div>
                                <input
                                    type="range" min="0.5" max="3" step="0.1"
                                    value={cursorSettings.size}
                                    onChange={(e) => updateCursorSettings({ size: parseFloat(e.target.value) })}
                                    className="w-full accent-brand-500 h-1.5 rounded-full appearance-none bg-white/10
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-400 [&::-webkit-slider-thumb]:cursor-pointer"
                                />
                            </div>

                            {/* Cursor Color */}
                            <div className="space-y-2">
                                <label className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Cursor Color</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={cursorSettings.color}
                                        onChange={(e) => updateCursorSettings({ color: e.target.value })}
                                        className="w-8 h-8 rounded-lg border border-white/10 bg-transparent cursor-pointer [&::-webkit-color-swatch-wrapper]:p-1 [&::-webkit-color-swatch]:rounded"
                                    />
                                    <input
                                        type="text"
                                        value={cursorSettings.color}
                                        onChange={(e) => updateCursorSettings({ color: e.target.value })}
                                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-xs text-white font-mono outline-none focus:border-brand-500/50"
                                    />
                                </div>
                            </div>

                            {/* Smoothing */}
                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Smoothing</label>
                                    <span className="text-[11px] text-txt-dim tabular-nums">{Math.round(cursorSettings.smoothing * 100)}%</span>
                                </div>
                                <input
                                    type="range" min="0" max="1" step="0.05"
                                    value={cursorSettings.smoothing}
                                    onChange={(e) => updateCursorSettings({ smoothing: parseFloat(e.target.value) })}
                                    className="w-full accent-brand-500 h-1.5 rounded-full appearance-none bg-white/10
                                        [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-400 [&::-webkit-slider-thumb]:cursor-pointer"
                                />
                            </div>
                            </>
                            )}

                            <div className="border-t border-white/5" />

                            {/* Effects */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <Eye size={14} className="text-brand-400" />
                                    <span className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Effects</span>
                                </div>

                                {/* Spotlight */}
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-txt-secondary">Spotlight highlight</span>
                                    <button
                                        onClick={() => updateCursorSettings({ spotlight: !cursorSettings.spotlight })}
                                        className={`w-9 h-5 rounded-full transition-colors relative ${cursorSettings.spotlight ? 'bg-brand-500' : 'bg-white/10'}`}
                                    >
                                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${cursorSettings.spotlight ? 'left-[18px]' : 'left-0.5'}`} />
                                    </button>
                                </div>

                                {cursorSettings.spotlight && (
                                    <div className="space-y-3 p-3 bg-surface-800/50 rounded-xl border border-white/5">
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="text-[10px] text-txt-dim">Radius</label>
                                                <span className="text-[10px] text-txt-dim tabular-nums">{Math.round(cursorSettings.spotlightRadius * 100)}%</span>
                                            </div>
                                            <input
                                                type="range" min="0.05" max="0.5" step="0.01"
                                                value={cursorSettings.spotlightRadius}
                                                onChange={(e) => updateCursorSettings({ spotlightRadius: parseFloat(e.target.value) })}
                                                className="w-full accent-brand-500 h-1.5 rounded-full appearance-none bg-white/10
                                                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-400 [&::-webkit-slider-thumb]:cursor-pointer"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <label className="text-[10px] text-txt-dim">Darkness</label>
                                                <span className="text-[10px] text-txt-dim tabular-nums">{Math.round(cursorSettings.spotlightOpacity * 100)}%</span>
                                            </div>
                                            <input
                                                type="range" min="0.1" max="0.9" step="0.05"
                                                value={cursorSettings.spotlightOpacity}
                                                onChange={(e) => updateCursorSettings({ spotlightOpacity: parseFloat(e.target.value) })}
                                                className="w-full accent-brand-500 h-1.5 rounded-full appearance-none bg-white/10
                                                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-400 [&::-webkit-slider-thumb]:cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="border-t border-white/5" />

                            {/* Click Effects */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <MousePointerClick size={14} className="text-brand-400" />
                                    <span className="text-[11px] font-medium text-txt-secondary uppercase tracking-wider">Click Effects</span>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-txt-secondary">Highlight clicks</span>
                                    <button
                                        onClick={() => updateCursorSettings({ highlightClicks: !cursorSettings.highlightClicks })}
                                        className={`w-9 h-5 rounded-full transition-colors relative ${cursorSettings.highlightClicks ? 'bg-brand-500' : 'bg-white/10'}`}
                                    >
                                        <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${cursorSettings.highlightClicks ? 'left-[18px]' : 'left-0.5'}`} />
                                    </button>
                                </div>

                                {cursorSettings.highlightClicks && (
                                    <>
                                        <div className="grid grid-cols-4 gap-1.5">
                                            {CLICK_EFFECTS.map((effect) => (
                                                <button
                                                    key={effect.value}
                                                    onClick={() => updateCursorSettings({ clickEffect: effect.value })}
                                                    className={`py-2 px-1 rounded-xl text-[10px] font-medium transition-all ${cursorSettings.clickEffect === effect.value
                                                        ? 'bg-brand-500/15 border border-brand-500/40 text-white'
                                                        : 'bg-white/5 border border-transparent text-txt-dim hover:bg-white/10'
                                                        }`}
                                                >
                                                    {effect.label}
                                                </button>
                                            ))}
                                        </div>

                                        {cursorSettings.clickEffect !== 'none' && (
                                            <>
                                                <div className="flex items-center gap-2">
                                                    <label className="text-[10px] text-txt-dim min-w-[40px]">Color</label>
                                                    <input
                                                        type="color"
                                                        value={cursorSettings.clickEffectColor}
                                                        onChange={(e) => updateCursorSettings({ clickEffectColor: e.target.value })}
                                                        className="w-6 h-6 rounded border border-white/10 bg-transparent cursor-pointer [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded"
                                                    />
                                                    <input
                                                        type="text"
                                                        value={cursorSettings.clickEffectColor}
                                                        onChange={(e) => updateCursorSettings({ clickEffectColor: e.target.value })}
                                                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white font-mono outline-none focus:border-brand-500/50"
                                                    />
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <label className="text-[10px] text-txt-dim min-w-[40px]">Size</label>
                                                    <input
                                                        type="range" min="1" max="3" step="0.5"
                                                        value={cursorSettings.clickEffectSize}
                                                        onChange={(e) => updateCursorSettings({ clickEffectSize: parseFloat(e.target.value) })}
                                                        className="flex-1 accent-brand-500 h-1 rounded-full appearance-none bg-white/10
                                                            [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-brand-400 [&::-webkit-slider-thumb]:cursor-pointer"
                                                    />
                                                    <span className="text-[10px] text-txt-dim tabular-nums w-8 text-right">
                                                        {cursorSettings.clickEffectSize === 1 ? 'S' : cursorSettings.clickEffectSize === 2 ? 'M' : 'L'}
                                                    </span>
                                                </div>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </>
            )}
        </div>
    )
}
